import { handleSingleResult } from "@strtmn/strtmn"
import type { SupabaseClient } from "@supabase/supabase-js"
import { isProd } from "../config"
import type { Database, TablesInsert, TablesUpdate } from "../supabase"
import {
  FeedComponentDefaultGroup,
  isWaterGroup,
} from "../recipes/FeedComponent"
import { createSupabase } from "../SupabaseClient"
import { ensureTimezone, type UUID } from "../utils"
import { type AddressDto } from "./Address"
import { type OrganizationDto, type OrgSettingsUpdateDto } from "./Organization"

export type DbOrganizationsInsert = TablesInsert<"Organizations">
export type DbOrganizationsUpdate = TablesUpdate<"Organizations">

export interface IOrgRepository {
  create(principalId: UUID, orgSettings: OrgSettingsUpdateDto): Promise<boolean>
  getById(orgId: UUID): Promise<OrganizationDto | null>
  listAllOrgs(): Promise<OrganizationDto[]>
  updateSettings(
    principalId: UUID,
    orgId: UUID,
    data: DbOrganizationsUpdate
  ): Promise<boolean>
}

export class OrgRepository implements IOrgRepository {
  private readonly supabase: SupabaseClient<Database>

  constructor() {
    this.supabase = createSupabase()
  }

  async create(
    principalId: UUID,
    orgSettings: OrgSettingsUpdateDto
  ): Promise<boolean> {
    console.debug("> OrgRepository.create")
    const { data, error } = await this.supabase
      .from("Organizations")
      .insert({
        ...orgSettings,
        address:
          typeof orgSettings.address === "string"
            ? orgSettings.address
            : JSON.stringify(orgSettings.address),
        created_by: principalId,
        updated_by: principalId,
      })
      .select()
    /** @todo use handleSingleResult? */
    if (error) {
      console.log("db error", error)
      return false
    }
    console.log("db success", data)
    return true
  }

  async getById(orgId: UUID): Promise<OrganizationDto | null> {
    console.log(`> OrgRepo.getById(${orgId})`)
    const supaResponse = await this.supabase
      .from("Organizations")
      .select(
        `
      *,
      Ausladegruppen (
        *
      ),
      FeedComponents (
        *
      ),
      Recipes (
        *,
        RecipesAusladegruppen (
          *
        ),
        RecipeComponents (
          *
        )
      )
    `
      )
      .eq("id", orgId)
      .is("Ausladegruppen.deleted_at", null)
      .is("FeedComponents.deleted_at", null)
      .is("Recipes.deleted_at", null)
      /** @note Ordering should be done in the views, but the modem does not do it */
      .order("order", {
        referencedTable: "Recipes.RecipeComponents",
        ascending: true,
      })
      .order("order", {
        referencedTable: "Recipes.RecipesAusladegruppen",
        ascending: true,
      })

    const orgData = handleSingleResult(
      supaResponse,
      "organization"
    ) as OrganizationDto
    if (!orgData) {
      return null
    }

    /** @todo fetch and map machines */

    OrgRepository.postprocessOrgData(orgData)
    return orgData
  }

  async listAllOrgs(): Promise<OrganizationDto[]> {
    console.debug("> OrgRepository.listAllOrgs")
    const { data, error } = await this.supabase
      .from("Organizations")
      .select("*")

    if (error) {
      console.error(
        "Die Abfrage lieferte einen Fehler. " + JSON.stringify(error, null, 2)
      )
      throw error
    }

    if (!data) {
      const basicMessage = "Keine Orgs gefunden."
      const devMessage =
        basicMessage + " error: " + JSON.stringify(error, null, 2)
      console.warn(devMessage)
      return []
    }
    return this.mapToOrgs(data)
  }

  async updateSettings(
    principalId: UUID,
    orgId: UUID,
    orgData: DbOrganizationsUpdate
  ): Promise<boolean> {
    console.debug(
      `> OrgRepository.updateSettings(org_name: ${orgData.name}, org_id: ${orgData.id})`
    )
    orgData.updated_by = principalId
    orgData.id = orgId
    const { data, error } = await this.supabase
      .from("Organizations")
      .update(orgData)
      .eq("id", orgId)
      .select()
    if (error) {
      console.log("db error", error)
      return false
    }
    console.log("db success", data)
    return true
  }

  static postprocessOrgData = (orgData: OrganizationDto) => {
    console.debug(
      `> OrgRepository.postprocessOrgData(org_name: ${orgData.name}, org_id: ${orgData.id})`
    )
    ensureTimezone(orgData)
    orgData.Ausladegruppen.forEach((ag) => {
      ensureTimezone(ag)
    })
    orgData.FeedComponents.forEach((fc) => {
      ensureTimezone(fc)
    })
    orgData.Recipes.forEach((r) => {
      ensureTimezone(r)
      // RecipeComponents and RecipesAusladegruppen, and Portions don't have timestamps
    })

    // Parse address if it's a string
    if (orgData.address && typeof orgData.address === "string") {
      orgData.address = JSON.parse(orgData.address)
    }
    // console.log(`Address ${orgData.name} -`, orgData.address)

    orgData.FeedComponents.forEach((fc) => {
      // `group` is nullable in the db (rows predating this field), but every
      // consumer -- UI and device-sync messages alike -- should see a concrete
      // value.
      fc.group = fc.group || FeedComponentDefaultGroup

      // Water has 0% dry matter by definition, regardless of what's stored.
      if (isWaterGroup(fc.group)) {
        fc.ts_gehalt_percent = 0
      }

      // `is_dry_mass` is derived per component from its own `ts_gehalt_percent`.
      fc.is_dry_mass = Number(fc.ts_gehalt_percent) > 0
    })
  }

  private mapToOrg(
    data: any /*Database['public']['Tables']['Organizations']['Row']*/
  ): OrganizationDto {
    console.debug("> mapToOrg")
    !isProd && console.debug("data =", data)
    if (data.address && typeof data.address === "string") {
      console.log("data.address is a string. Parse it.")
      try {
        const _address = JSON.parse(data.address)
        const _addressData: AddressDto = {
          street: _address.street,
          number: _address.number,
          zip: _address.zip,
          city: _address.city,
          country: _address.country,
        }
        if (_address.gps && _address.gps.lat && _address.gps.lng) {
          _addressData.gps = {
            lat: _address.gps.lat,
            lng: _address.gps.lng,
          }
        }
        data.address = _addressData
      } catch (err) {
        console.warn("Could not parse address:", data.address)
      }
    }

    const orgData: OrganizationDto = {
      id: data.id,
      name: data.name,
      // type: data.type,
      address: data.address,
      // latitude: data.latitude,
      // longitude: data.longitude,
      // parentId: data.parent_id,
      user_role: data.role,
      created_at: data.created_at,
      created_by: data.created_by,
      updated_at: data.updated_at,
      updated_by: data.updated_by,
      Ausladegruppen: [],
      FeedComponents: [],
      Recipes: [],
      Machines: [],
    }

    // if (
    //   data.machines &&
    //   Array.isArray(data.machines) &&
    //   data.machines.length > 0
    // ) {
    //   orgData.machines = data.machines.map((m: any) => {
    //     const mappedMachine = MachineRepository.mapToMachine(m)
    //     return mappedMachine
    //   })
    // }

    // if (data.users && Array.isArray(data.users) && data.users.length > 0) {
    //   orgData.Users = data.users.map((u: any) => {
    //     const mappedUser = UserRepository.mapToUser(u)
    //     if (u.user_role) {
    //       /** @ts-ignore */
    //       mappedUser.user_role = u.user_role
    //     }
    //     return mappedUser
    //   })
    // }

    !isProd && console.debug("mapToOrg: mapped =", orgData)
    return orgData
  }

  private mapToOrgs(
    data: any /*Database['public']['Tables']['Organizations']['Row']*/
  ): OrganizationDto[] {
    return data.map((d: any) => this.mapToOrg(d))
  }
}
